// `$v7` without putting all ~2,100 v7 icons in the entry bundle.
//
// Pages start on the generated core set (icons/v7-core.js: every icon the source
// references by name). The full set (icons/v7.js) is a separate chunk, loaded by
// the routes that resolve icons by name at runtime, by `preloadFullV7WhenIdle`
// on every other page, and by any lookup the core set misses. Only a miss reads
// the shallowRef, so a render that read a missing icon re-renders once the full
// set arrives (a late icon, never a blank one), while a render that found its
// icons in the core set does not: both sets hold the same icon objects.
import { shallowRef } from 'vue'
import core from 'app/icons/v7-core'

let current = core
const active = shallowRef(core)
let loading = null

// Vue's own checks (isRef, isReactive, toRaw) and JSON.stringify probe for these
// names; they are not icons, so they must not start the full-set download.
const isLookup = (name) => typeof name === 'string' && !name.startsWith('__v_') && name !== 'toJSON'

// A miss subscribes the render to the active set and starts the full-set load.
function miss() {
  void active.value
  loadFullV7()
}

export function loadFullV7() {
  if (!loading) {
    // Resolves with nothing: returning the icon set from here would make the
    // promise read `.then` on it, which the set's fallback proxy takes for a
    // missing style and warns about. Never rejects: a failed chunk leaves the
    // page on the core set (routes still open) and the next call retries.
    loading = import(/* webpackChunkName: "icons-v7-full" */ 'app/icons/v7')
      .then((mod) => {
        current = mod.default
        active.value = mod.default
      })
      .catch(() => {
        loading = null
      })
  }
  return loading
}

export const isFullV7Loaded = () => current !== core

// Off the critical path: after the page has loaded and the browser is idle.
export function preloadFullV7WhenIdle() {
  if (typeof window === 'undefined') return

  // Guarded: Safari has no requestIdleCallback and gets setTimeout instead.
  // eslint-disable-next-line compat/compat
  const start = () => (window.requestIdleCallback ? window.requestIdleCallback(() => loadFullV7()) : setTimeout(loadFullV7, 1))

  if (document.readyState === 'complete') start()
  else window.addEventListener('load', start, { once: true })
}

// Only the core set needs a miss handler; the full set falls back on its own.
const styleProxies = new WeakMap()

function coreStyle(icons) {
  if (!styleProxies.has(icons)) {
    styleProxies.set(
      icons,
      new Proxy(icons, {
        get(target, name) {
          if (isLookup(name) && !(name in target)) miss()
          return target[name]
        }
      })
    )
  }
  return styleProxies.get(icons)
}

const EMPTY_STYLE = {}

export const v7 = new Proxy(
  {},
  {
    get(_, style) {
      if (current !== core || !isLookup(style)) return current[style]

      if (!(style in core)) miss()
      return coreStyle(core[style] || EMPTY_STYLE)
    },
    has(_, style) {
      return style in active.value
    },
    ownKeys() {
      return Reflect.ownKeys(active.value)
    },
    getOwnPropertyDescriptor(_, style) {
      const descriptor = Reflect.getOwnPropertyDescriptor(active.value, style)
      // The proxy target is an empty object, so a non-configurable descriptor would break the Proxy invariants.
      return descriptor && { ...descriptor, configurable: true }
    }
  }
)
